// Activity Logger Utility
// Manages recent activity tracking in localStorage

export interface Activity {
  id: string;
  type: 'created' | 'updated' | 'deleted' | 'toggled';
  ruleName: string;
  action: string;
  description: string;
  timestamp: string;
  icon: 'plus' | 'edit' | 'trash' | 'toggle';
  color: 'emerald' | 'cyan' | 'red' | 'amber';
}

const ACTIVITIES_KEY = 'weather_ads_activities';
const MAX_ACTIVITIES = 3;

/**
 * Get recent activities from localStorage
 */
export const getActivities = (): Activity[] => {
  try {
    const stored = localStorage.getItem(ACTIVITIES_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch (error) {
    console.error('Failed to read activities from localStorage:', error);
    return [];
  }
};

/**
 * Add a new activity to localStorage (keeps only last 3)
 */
export const addActivity = (activity: Omit<Activity, 'id' | 'timestamp'>): void => {
  try {
    const activities = getActivities();
    const newActivity: Activity = {
      ...activity,
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
    };

    // Add new activity at the beginning and keep only last 3
    const updatedActivities = [newActivity, ...activities].slice(0, MAX_ACTIVITIES);

    localStorage.setItem(ACTIVITIES_KEY, JSON.stringify(updatedActivities));

    // Dispatch custom event so Dashboard can listen for updates
    window.dispatchEvent(new CustomEvent('activitiesUpdated', { detail: updatedActivities }));
  } catch (error) {
    console.error('Failed to save activity to localStorage:', error);
  }
};

/**
 * Clear all activities
 */
export const clearActivities = (): void => {
  try {
    localStorage.removeItem(ACTIVITIES_KEY);
    window.dispatchEvent(new CustomEvent('activitiesUpdated', { detail: [] }));
  } catch (error) {
    console.error('Failed to clear activities:', error);
  }
};

/**
 * Format timestamp for display (e.g., "2 minutes ago")
 */
export const formatTimeAgo = (timestamp: string): string => {
  const now = new Date();
  const activityTime = new Date(timestamp);
  const diffMs = now.getTime() - activityTime.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
};
